import { useFeatureFlags } from '@/app/composable';
import { AssetTypeId, StatusCode } from '@/modules/asset/constants';
import { RetrievalAccessibility } from '@/modules/retrieval/constants/retrieval-accessibility.constants';
import { computed, Ref } from '@vue/composition-api';
import * as R from 'ramda';
import { Asset } from '../types';

export function useAssetRetrieval(
    asset: Ref<Asset | undefined>,
    user: Ref<{ id: number; organisationId: number }>,
    contract: Ref<{ id: string; offlineRetention: string[] } | null>,
    acquiredAssetIds: Ref<number[]>,
) {
    const { isEnabled } = useFeatureFlags();

    const status = computed(() => asset.value?.status);

    const isModel = computed(() => asset.value?.assetTypeId === AssetTypeId.Model);

    const isOther = computed(() => asset.value?.structure?.type === 'other' || !asset.value?.structure?.primaryConcept);

    const isAcquiredAndNotOther = computed(() => !!contract.value && !isOther.value);

    const isLocal = computed<boolean>(() => {
        if (asset.value) {
            if (!R.isNil(asset.value.isLocal)) return asset.value.isLocal;
            return !!asset.value.metadata?.filenames?.length;
        }
        return false;
    });

    const belongsToUserOrOrganisation = computed(() => {
        if (asset && user.value) {
            if (user.value.organisationId && asset.value?.createdBy.organisationId)
                return user.value.organisationId === asset.value?.createdBy.organisationId;
            return user.value.id === asset.value?.createdBy.id;
        }
        return true;
    });

    const accessibilityKeys = computed(() => {
        const keys: RetrievalAccessibility[] = [];

        if (belongsToUserOrOrganisation.value && asset.value?.metadata?.distribution?.accessibility) {
            const accessibility = asset.value.metadata.distribution.accessibility;
            if (accessibility.includes('Through an API') && isEnabled('retrieve.api'))
                keys.push(RetrievalAccessibility.API);
            if (accessibility.includes('Through a data streaming mechanism') && isEnabled('retrieve.stream'))
                keys.push(RetrievalAccessibility.STREAMING);
            if (accessibility.includes('As a downloadable file') && isEnabled('retrieve.files'))
                keys.push(RetrievalAccessibility.FILE);
        } else if (contract.value?.offlineRetention) {
            const offlineRetention = contract.value.offlineRetention;
            if (offlineRetention.includes('Through a Custom API') && isEnabled('retrieve.api'))
                keys.push(RetrievalAccessibility.API);
            if (
                offlineRetention.includes('As an (Encrypted) File') &&
                isEnabled('retrieve.files') &&
                (isOther.value || (asset.value && !acquiredAssetIds.value.includes(asset.value.id)))
            )
                keys.push(RetrievalAccessibility.FILE);
        }

        return keys;
    });

    const canRetrieve = computed(() => !isModel.value && accessibilityKeys.value.length > 0);

    const isRetrievalDisabled = computed(() =>
        status.value
            ? ![StatusCode.Internal, StatusCode.Pending, StatusCode.Available].includes(status.value) || isLocal.value
            : true,
    );

    const retrievalTooltip = computed(() => {
        if (isRetrievalDisabled.value) {
            if (status.value === StatusCode.Deleted)
                return 'A retrieval query cannot be configured as the dataset has been deleted';
            if (status.value === StatusCode.Uploading)
                return 'A retrieval query cannot be configured until the dataset becomes available';
            if (isLocal.value) return 'A retrieval query cannot be configured as the dataset has been generated locally';
            return "A retrieval query cannot be configured until the dataset's metadata are filled in and it becomes available in the platform";
        }
        return 'Retrieve this dataset by configuring a Retrieval Query';
    });

    return {
        retrievalTooltip,
        isRetrievalDisabled,
        belongsToUserOrOrganisation,
        accessibilityKeys,
        isModel,
        canRetrieve,
        isOther,
        isAcquiredAndNotOther,
    };
}
